import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
// this is not used directly, but has to be included
// noinspection ES6UnusedImports
import momentDurationFormatSetup from 'moment-duration-format'; // eslint-disable-line no-unused-vars

import fetchSalon from 'actions/fetchSalon.action';
import beFetchStylistStripeDashboard from 'actions/stylist/beFetchStylistStripeDashboard.action';
import beSetupStylistStripeAccount from 'actions/stylist/beSetupStylistStripeAccount.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { feBusinessStripeFoldToggle, feStripeFoldToggle, feTipsFoldToggle } from 'actions/ui.actions';
import ICO_BOOKING from 'assets/images/icons/general-settings.svg';
import ICO_PAYMENT from 'assets/images/icons/pay-black.svg';
import ICO_DESCRIPTION from 'assets/images/icons/pencil-note.svg';
import ICO_SETTINGS from 'assets/images/online-booking/General-purple.svg';
import ICO_HOURS from 'assets/images/online-booking/Hours.svg';
import IMG_AVATAR from 'assets/images/online-booking/no-image.png';
import ICO_SERVICES from 'assets/images/online-booking/Services.svg';
import ToggleFeature from 'components/core/ToggleFeature.component';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import StripeStatusBadges from 'components/settings/StripeStatusBadges.component';
import PUSHER from 'config/pusher.singleton';
import { CURRENCY_BORDER_ICONS } from 'constants/currency.const';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import TRANSFER_POLICY from 'constants/pos/stripeTransferPolicy.const';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import {
  selectCurrentSalonBetaAnnualSalon,
  selectCurrentSalonBilling,
  selectCurrentSalonId,
  selectCurrentSalonNoShowProtection,
  selectCurrentSalonStripeAccount,
  selectCurrentSalonStylists,
} from 'selectors/currentSalon.selectors';
import { selectBookingStripeAccountStatus } from 'selectors/settings.selectors';
import { selectBusinessStripeFoldoutOpen, selectStripeFoldoutOpen, selectTipsFoldoutOpen } from 'selectors/ui.selectors';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromObjectToKeys from 'utils/converters/object/fromObjectToKeys.util';
import { formatHourAndMinuteShortMeridiem } from 'utils/date/format.util';
import isTouchDevice from 'utils/dom/isTouchDevice.util';
import { locationReplace } from 'utils/dom/location.util';
import pageScrollToTop from 'utils/dom/pageScrollToTop.util';
import delayed from 'utils/functions/delayed.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import C from 'utils/values/constant.util';

import PopupModalContainer from 'common/PopupModal';
import { SALON_STRIPE_SETUP } from 'lib/constants/currentSalon';
import { getCategories, removeFBIntegration, toggleOnlineBoarding } from 'salon/actions';
import beGetSettingsNcob from 'settings/actions/beGetSettingsNcob';
import {
  changeIsOnline,
  changeIsOpen,
  changePatchTest,
  clearErrorMessage,
  getSalonOnlineSettings,
  saveSalonOnlineSettings,
  setSalonNoshowOrDeposit,
  stylistCategoryChangeIsOnline as stylistCategoryChangeIsOnlineAction,
  toggleEverythingOffline,
} from 'settings/online-booking/actions';
import ModalSetupOnlineBooking from 'settings/online-booking/modals/ModalSetupOnlineBooking';
import SetupSelfEmployedModal from 'settings/online-booking/modals/SetupSelfEmployedModal';
import StripeSetupNoticeModal from 'settings/online-booking/modals/StripeSetupNoticeModal.component';
import StaffTips from 'settings/online-booking/pages/settings/StaffTips.component';
import PieChart from 'settings/online-booking/PieChart.component';
import BookingCode from 'settings/page-elements/booking-code/BookingCode';
import ConfirmationModal from 'settings/page-elements/ConfirmationModal';
import DropdownPatchTest from 'settings/page-elements/dropdown/DropdownPatchTest';
import DropdownService from 'settings/page-elements/dropdown/DropdownService';
import DropdownServiceWithoutSwitcher from 'settings/page-elements/dropdown/DropdownServiceWithoutSwitcher';
import FacebookConnect from 'settings/page-elements/FacebookConnect';
import DepositSetup from 'settings/page-elements/setup-nsp-deposit/DepositSetup';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';
import NoShowProtectionSetup from 'settings/page-elements/setup-nsp-deposit/NoShowProtectionSetup';
import RequestDepositSetup from 'settings/page-elements/setup-nsp-deposit/RequestDepositSetup';
import ServicesDescription from 'settings/services/ServicesDescription.component';

import NoBillingModal from './modals/NoBillingModal';

import CN from './SetUpView.module.scss';

const L = logger('SetUpView');

const ONE_STYLIST = 1;

const DISPLAY_BUTTON = false;
const USE_POLICY_SECTION = false;
const RENDER_ERROR_MODAL = false;

const LINK_POLICY = 'https://gettingslick.typeform.com/to/Qzbj8C#company_name=xxxxx';

// NOTE: we send numbers to the BE, but it returns strings that we need to map to these values
const TRANSFER_POLICY_MAP = C({
  NO_TRANSFERS: TRANSFER_POLICY.noTransfers,
  SELF_EMPLOYED_ALL_TRANSFERS: TRANSFER_POLICY.allTransfers, // 20
  SELF_EMPLOYED_SPLIT_COMMISSIONS: TRANSFER_POLICY.splitCommissions, // 30
  EMPLOYED_TIP_ONLY: TRANSFER_POLICY.tipOnly, // 40
});

const getShortDay = (fullName) => {
  const name = fullName.split(' ')[0];
  switch (name) {
    case 'Monday': {
      return 'common.days.mon';
    }
    case 'Tuesday': {
      return 'common.days.tue';
    }
    case 'Wednesday': {
      return 'common.days.wed';
    }
    case 'Thursday': {
      return 'common.days.thu';
    }
    case 'Friday': {
      return 'common.days.fri';
    }
    case 'Saturday': {
      return 'common.days.sat';
    }
    case 'Sunday': {
      return 'common.days.sun';
    }
    default:
      return '';
  }
};

const getFullDayName = (shortName) => {
  switch (shortName) {
    case 'mon': {
      return 'common.days.monday';
    }
    case 'tue': {
      return 'common.days.tuesday';
    }
    case 'wed': {
      return 'common.days.wednesday';
    }
    case 'thu': {
      return 'common.days.thursday';
    }
    case 'fri': {
      return 'common.days.friday';
    }
    case 'sat': {
      return 'common.days.saturday';
    }
    case 'sun': {
      return 'common.days.sunday';
    }
    default:
      return '';
  }
};

const generateDates = (settingsData, t) => {
  // if index start with 0, checkboxes in patch test has same id as in business hours
  // clicking on patch test items with check/uncheck days, so start from 200
  let index = 200;
  const duration = moment.duration();
  const dayItems = [];
  const keys = Object.keys(settingsData.business_hours);

  let dayCount = 0;

  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const day = settingsData.business_hours[key];
    if (day) {
      const a = moment.utc(day.open_hr, 'hh:mm:ss');
      const b = moment.utc(day.close_hr, 'hh:mm:ss');
      if (day.is_open) {
        dayCount += 1;
        duration.add(b.diff(a));
      }

      const formattedA = formatHourAndMinuteShortMeridiem(a);
      const formattedB = formatHourAndMinuteShortMeridiem(b);

      dayItems.push({
        id: (index += 1),
        name: `${t(getFullDayName(key))} ${formattedA} - ${formattedB}`,
        is_open: day.is_open,
      });
    }
  }

  return { duration, dayItems, dayCount };
};

const countOnlineServices = (settingsData) => {
  let shouldBreak = false;
  let servicesCount = 0;
  const stylistIds = Object.keys(settingsData.stylist_service_data);
  for (let i = 0; i < stylistIds.length; i += 1) {
    if (shouldBreak) {
      break;
    }
    const stylist = settingsData.stylist_service_data[stylistIds[i]];
    const categoryIds = stylist.categories ? Object.keys(stylist.categories) : [];
    for (let j = 0; j < categoryIds.length; j += 1) {
      if (shouldBreak) {
        break;
      }
      const category = stylist.categories[categoryIds[j]];
      for (let k = 0; k < category.length; k += 1) {
        if (shouldBreak) {
          break;
        }
        if (category[k].is_online) {
          servicesCount += 1;
          if (servicesCount >= 1) {
            shouldBreak = true;
          }
        }
      }
    }
  }
  return servicesCount;
};

const SetUpView = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isSalonInGB } = useGilt();
  const { isDarkMode } = useTheme();

  // Redux selectors
  const hasNoShowProtection = useSelector(selectCurrentSalonNoShowProtection);
  const isBusinessStripeFoldoutOpen = useSelector(selectBusinessStripeFoldoutOpen);
  const isStripeFoldoutOpen = useSelector(selectStripeFoldoutOpen);
  const isTipsFoldoutOpen = useSelector(selectTipsFoldoutOpen);
  const stripeAccountStatus = useSelector(selectBookingStripeAccountStatus);
  const hasBetaAnnualSalon = useSelector(selectCurrentSalonBetaAnnualSalon);
  const hasStripeAccount = useSelector(selectCurrentSalonStripeAccount);
  const hasBilling = useSelector(selectCurrentSalonBilling);
  const salonId = useSelector(selectCurrentSalonId);
  const stylists = useSelector(selectCurrentSalonStylists);
  const settingsData = useSelector((state) => state.settings.onlineBooking.settingsData);
  const salon_ncob_text = useSelector((state) => state.currentSalon.salon_ncob_text);
  const ncob_tiny_url = useSelector((state) => state.currentSalon.ncob_tiny_url || state.settings.onlineBooking.settingsData.ncob_tiny_url);
  const ncob_deposit_settings = useSelector((state) => state.currentSalon.ncob_deposit_settings);
  const ncob_custom_amount = useSelector((state) => state.currentSalon.ncob_custom_amount);
  const no_show_protection = useSelector((state) => state.currentSalon.no_show_protection);
  const has_completed_online_boarding = useSelector((state) => state.currentSalon.has_completed_online_boarding);
  const beta_annual_salon = useSelector((state) => state.currentSalon.beta_annual_salon);
  const has_billing = useSelector((state) => state.currentSalon.has_billing);
  const initialServerData = useSelector((state) => state.settings.onlineBooking.initialServerData);
  const isFetching = useSelector((state) => state.settings.onlineBooking.isFetching);
  const errorMessageRedux = useSelector((state) => state.settings.onlineBooking.errorMessage);
  const currentSalon = useSelector((state) => state.currentSalon);
  const areSettingsSaved = useSelector((state) => state.settings.onlineBooking.areSettingsSaved);
  const settingStarted = useSelector((state) => state.settings.onlineBooking.settingStarted);
  const group_ncob_tiny_url = useSelector((state) => state.currentSalon.group_ncob_tiny_url);

  // Initial data
  const initialData = useMemo(() => generateDates(settingsData, t), []); // eslint-disable-line react-hooks/exhaustive-deps

  // State
  const [dayCount, setDayCount] = useState(initialData.dayCount);
  const [dayItems, setDayItems] = useState(initialData.dayItems);
  const [confirmDaysOfflineModal, setConfirmDaysOfflineModal] = useState(false);
  const [confirmOnlineModal, setConfirmOnlineModal] = useState(false);
  const [renderStripeSetupNoticeModal, setRenderStripeSetupNoticeModal] = useState(false);
  const [lastDay, setLastDay] = useState('');
  const [confirmServicesOfflineModal, setConfirmServicesOfflineModal] = useState(false);
  const [no_show_protection_state, setNoShowProtectionState] = useState(no_show_protection);
  const [confirmCategoryModal, setConfirmCategoryModal] = useState(false);
  const [isSalonOnline, setIsSalonOnline] = useState(currentSalon.online_booking);
  const [popupMessage, setPopupMessage] = useState('');
  const [hasChanged, setHasChanged] = useState(false);
  const [confirmOfflineModal, setConfirmOfflineModal] = useState(false);
  const [componentFirstMounted, setComponentFirstMounted] = useState(false);
  const [hasOnlineService, setHasOnlineService] = useState(false);
  const [hasOpenDay, setHasOpenDay] = useState(false);
  const [errorEnableModal, setErrorEnableModal] = useState(false);
  const [enableMessage, setEnableMessage] = useState('');
  const [showBillingPop, setShowBillingPop] = useState(false);
  const [isSetupSelfEmployedRendered, setIsSetupSelfEmployedRendered] = useState(false);
  const [selectedStylistId, setSelectedStylistId] = useState(null);
  const [isHiddenMap, setIsHiddenMap] = useState({});
  const [stripeModal, setStripeModal] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [parentContainerLength, setParentContainerLength] = useState(undefined);
  const [childContainerLength, setChildContainerLength] = useState(undefined);

  const parentContainerRef = useRef(null);
  const childContainerRef = useRef(null);
  const hasChangedRef = useRef(hasChanged);
  hasChangedRef.current = hasChanged;

  // Helper functions
  const updateContainerLengths = useCallback(() => {
    const parentContainer = parentContainerRef.current;
    const childContainer = childContainerRef.current;

    if (parentContainer && childContainer) {
      setParentContainerLength(parentContainer.offsetWidth);
      setChildContainerLength(childContainer.offsetWidth);
    }
  }, []);

  const resolveAfterSaved = useCallback(
    () =>
      new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!isFetching) {
            resolve('resolved');
            dispatch(fetchSalon(salonId));
            clearInterval(interval);
          }
        }, 200);
      }),
    [isFetching, salonId, dispatch],
  );

  const asyncCall = useCallback(async () => void (await resolveAfterSaved()), [resolveAfterSaved]);

  const saveChanges = useCallback(
    (tf) => {
      if (!currentSalon.has_completed_online_boarding) {
        dispatch(toggleOnlineBoarding());
      }
      dispatch(saveSalonOnlineSettings(salonId, settingsData, initialServerData, tf));
      if (!initialServerData.online_booking && settingsData.online_booking) {
        asyncCall();
      }
    },
    [currentSalon.has_completed_online_boarding, salonId, settingsData, initialServerData, dispatch, asyncCall],
  );

  const checkButton = useCallback(() => {
    let activeService;
    let activeDay;

    activeDay = false;
    const days = Object.keys(settingsData.business_hours);
    for (let i = 0; i < days.length; i += 1) {
      const day = settingsData.business_hours[days[i]];
      if (day && day.is_open) {
        activeDay = true;
        break;
      }
    }
    activeService = false;
    const stylistIds = Object.keys(settingsData.stylist_service_data);
    for (let i = 0; i < stylistIds.length; i += 1) {
      if (activeService) {
        break;
      }
      const stylist = settingsData.stylist_service_data[stylistIds[i]];
      const categoryNames = Object.keys(stylist.categories);
      for (let j = 0; j < categoryNames.length; j += 1) {
        if (activeService) {
          break;
        }
        const category = stylist.categories[categoryNames[j]];
        for (let k = 0; k < category.length; k += 1) {
          if (category[k].is_online) {
            activeService = true;
            break;
          }
        }
      }
    }
    if (activeDay && activeService) {
      if (!currentSalon.has_completed_online_boarding) {
        dispatch(toggleOnlineBoarding());
      }
    }
    if (hasChangedRef.current) {
      if (activeDay && activeService) {
        setTimeout(() => {
          setIsSalonOnline(true);
          saveChanges(true);
        }, 1);
      } else if (!activeDay) {
        dispatch(toggleEverythingOffline(false, 'day'));
        setTimeout(() => {
          setIsSalonOnline(false);
          saveChanges(false);
        }, 1);
      } else if (!activeService) {
        dispatch(toggleEverythingOffline(false, 'service'));
        setTimeout(() => {
          setIsSalonOnline(false);
          saveChanges(false);
        }, 1);
      }
    }
  }, [settingsData, currentSalon.has_completed_online_boarding, dispatch, saveChanges]);

  const fetchSettingsNcob = useCallback(async () => {
    try {
      const result = await dispatch(beGetSettingsNcob());

      if (result.is.ok) {
        L.debug('mount()', 'ncobHasStripe:', result.json.has_ncob_stripe_account);
      }
    } catch (error) {
      L.debug('fetchSettingsNcob()', 'error:', error);
    }
  }, [dispatch]);

  const subscribe = useCallback(
    () =>
      void PUSHER.redux.subscribe({
        channelName: `${salonId}_channel`,
        eventName: 'salon_stripe_setup',
        actionType: SALON_STRIPE_SETUP,
      }),
    [salonId],
  );

  const unsubscribe = useCallback(() => {
    PUSHER.redux.unsubscribe({
      channelName: `${salonId}_channel`,
      eventName: 'salon_stripe_setup',
      actionType: SALON_STRIPE_SETUP,
    });
  }, [salonId]);

  const setupStripeAccount = useCallback(
    async (stylistId, transferPolicy = null) => {
      const FTAG = 'setupStripeAccount()';

      if (!hasStripeAccount) {
        L.debug(FTAG, 'Aborting setup because hasStripeAccount:', hasStripeAccount);
        return;
      }

      setStripeModal(true);
      setIsSetupSelfEmployedRendered(false);
      setRenderStripeSetupNoticeModal(false);
      // if the status is incomplete we do not send the transferPolicy parameter
      const requestData = { stylistId, transferPolicy: transferPolicy ?? void 1 };
      const result = await dispatch(beSetupStylistStripeAccount(requestData));

      // browser might garbage collect objects, but strings remain in the console longer
      L.debug(FTAG, 'result text:', result?.text);

      setStripeModal(false);

      if (!result.is.ok) return;
      await dispatch(getSalonOnlineSettings(salonId)); // update UI

      const url = fromAnyToString(result?.json?.stripe_link);
      L.info(FTAG, 'Navigating to', url, '...');
      locationReplace({ url });
    },
    [hasStripeAccount, salonId, dispatch],
  );

  const viewStripeDashboard = useCallback(
    async (stylistId) => {
      const isTouch = isTouchDevice();

      setStripeModal(true);

      const result = await dispatch(beFetchStylistStripeDashboard({ stylistId }));

      if (result.is.ok) {
        setStripeModal(false);

        if (isTouch) {
          const newWindow = window.open(result.json.url, '_blank', 'noopener,noreferrer');
          if (newWindow) {
            newWindow.location.href = result.json.url;
          } else {
            window.location.href = result.json.url;
          }
        } else {
          window.open(result.json.url, '_blank', 'noopener,noreferrer');
        }
      } else {
        setStripeModal(false);
      }
    },
    [dispatch],
  );

  const showSelfEmployedModal = useCallback(
    (stylistId) => {
      const accountStatus = stripeAccountStatus?.[stylistId]?.stripe_account_status;
      if (accountStatus !== STRIPE_STATUS.incomplete && accountStatus !== STRIPE_STATUS.restricted) {
        setIsSetupSelfEmployedRendered(true);
        setSelectedStylistId(stylistId);
        setRenderStripeSetupNoticeModal(false);
      } else {
        setupStripeAccount(stylistId);
      }
    },
    [stripeAccountStatus, setupStripeAccount],
  );

  const closeErrorModal = useCallback(() => {
    setErrorEnableModal(false);
    setEnableMessage('');
  }, []);

  const refreshStylistData = useCallback(() => {
    L.unignore('refreshStylistData()', 'getSalonOnlineSettings:', dispatch(getSalonOnlineSettings(salonId)));
  }, [salonId, dispatch]);

  const confirmOnlineSalon = useCallback(() => {
    if (has_billing || beta_annual_salon) {
      setTimeout(() => {
        setIsSalonOnline(true);
        saveChanges(true);
        setConfirmOnlineModal(false);
        setPopupMessage('');
      }, 1);
    } else {
      setShowBillingPop(true);
      setConfirmOnlineModal(false);
    }
  }, [has_billing, beta_annual_salon, saveChanges]);

  const onNoShowCheckboxClick = useCallback(() => {
    if (ncob_deposit_settings !== 'null') {
      const msg = t('onlineBooking.generalSettings.enableMessage1');
      setErrorEnableModal(true);
      setEnableMessage(msg);
    } else if (ncob_deposit_settings === 'null') {
      const data = {
        ncob_deposit_settings,
        ncob_custom_amount,
        no_show_protection: !no_show_protection,
      };
      dispatch(setSalonNoshowOrDeposit(data, salonId.toString()));
      setNoShowProtectionState((prev) => !prev);
    }
  }, [ncob_deposit_settings, ncob_custom_amount, no_show_protection, salonId, dispatch, t]);

  const onDepositCheckboxClick = useCallback(() => {
    if (no_show_protection) {
      const msg = t('onlineBooking.generalSettings.enableMessage2');
      setErrorEnableModal(true);
      setEnableMessage(msg);
    }
    if (ncob_deposit_settings === 'null' && !no_show_protection) {
      const data = {
        ncob_deposit_settings,
        ncob_custom_amount,
        no_show_protection,
      };
      dispatch(setSalonNoshowOrDeposit(data, salonId.toString()));
    } else if (ncob_deposit_settings !== 'null' && !no_show_protection) {
      const data = {
        ncob_deposit_settings: 'null',
        ncob_custom_amount: 0,
        no_show_protection: no_show_protection_state,
      };
      dispatch(setSalonNoshowOrDeposit(data, salonId.toString()));
    }
  }, [ncob_deposit_settings, ncob_custom_amount, no_show_protection, no_show_protection_state, salonId, dispatch, t]);

  const onStylistServiceCheckboxClick = useCallback(
    (stylistId, serviceId, value) => {
      let servicesCount = 0;
      let shouldBreak = false;
      const stylistIds = Object.keys(settingsData.stylist_service_data);

      for (let i = 0; i < stylistIds.length; i += 1) {
        if (shouldBreak) {
          break;
        }
        const stylist = settingsData.stylist_service_data[stylistIds[i]];
        const categoryIds = Object.keys(stylist.categories);
        for (let j = 0; j < categoryIds.length; j += 1) {
          if (shouldBreak) {
            break;
          }
          const category = stylist.categories[categoryIds[j]];
          for (let k = 0; k < category.length; k += 1) {
            if (shouldBreak) {
              break;
            }
            if (category[k].is_online) {
              servicesCount += 1;
              if (servicesCount > 1) {
                shouldBreak = true;
              }
            }
          }
        }
      }

      if (!value && servicesCount === 1) {
        setHasChanged(true);
        setConfirmServicesOfflineModal(true);
        setPopupMessage(t('onlineBooking.generalSettings.confirmationModal.popupMessage1'));
        checkButton();
      } else {
        dispatch(changeIsOnline(stylistId, serviceId, value));
        setHasChanged(true);
        setHasOnlineService(true);
        setTimeout(() => checkButton(), 0);
      }
    },
    [settingsData, dispatch, t, checkButton],
  );

  const onDayCheckboxClick = useCallback(
    (name, id, value) => {
      const dayName = t(getShortDay(name));
      const days = Object.keys(settingsData.business_hours);
      let openCount = 0;
      let shouldSet = true;

      for (let i = 0; i < days.length; i += 1) {
        const day = settingsData.business_hours[days[i]];
        if (day && day.is_open) {
          openCount += 1;
        }
      }

      if (!value && openCount === 1) {
        shouldSet = false;
        const msg = t('onlineBooking.generalSettings.confirmationModal.popupMessage2');
        setConfirmDaysOfflineModal(true);
        setLastDay(dayName);
        setPopupMessage(msg);
      } else if (value && hasOnlineService) {
        setIsSalonOnline(true);
        setHasChanged(true);
        setHasOpenDay(true);
      }

      if (shouldSet) {
        dispatch(changeIsOpen(dayName, value));
        setHasChanged(true);
        setTimeout(() => checkButton(), 0);
      }
    },
    [settingsData, hasOnlineService, dispatch, t, checkButton],
  );

  const onPatchTestCheckboxClick = useCallback(
    (name, id, value, isSwitcher) => {
      dispatch(changePatchTest(id, value));
      setHasChanged(true);
      if ((isSwitcher && isSwitcher[0] === isSwitcher[1]) || !isSwitcher) {
        setTimeout(() => checkButton(), 0);
      }
    },
    [dispatch, checkButton],
  );

  const onSalonCheckBoxClick = useCallback(() => {
    if (isSalonOnline) {
      const msg = <div>{t('onlineBooking.generalSettings.confirmationModal.popupMessage3')}</div>;
      setConfirmOfflineModal(true);
      setPopupMessage(msg);
    }
    if (!has_completed_online_boarding) {
      setConfirmOnlineModal(true);
    } else {
      confirmOnlineSalon();
    }
  }, [isSalonOnline, has_completed_online_boarding, confirmOnlineSalon, t]);

  const stylistCategoryChangeIsOnlineHandler = useCallback(
    (stylistId, categoryName, value) => {
      let categoriesCount = 0;
      const stylistIds = Object.keys(settingsData.stylist_service_data);

      for (let i = 0; i < stylistIds.length; i += 1) {
        const stylist = settingsData.stylist_service_data[stylistIds[i]];
        const categoryIds = Object.keys(stylist.categories);
        for (let j = 0; j < categoryIds.length; j += 1) {
          const category = stylist.categories[categoryIds[j]];
          for (let k = 0; k < category.length; k += 1) {
            if (category[k].is_online) {
              categoriesCount += 1;
              break;
            }
          }
        }
      }

      if (!value && categoriesCount === 1) {
        setConfirmCategoryModal(true);
        setHasChanged(true);
        setPopupMessage(t('onlineBooking.generalSettings.confirmationModal.popupMessage1'));
        checkButton();
      } else {
        dispatch(stylistCategoryChangeIsOnlineAction(stylistId, categoryName, value));
        setHasChanged(true);
        setHasOnlineService(true);
        setTimeout(() => checkButton(), 0);
      }
    },
    [settingsData, dispatch, t, checkButton],
  );

  const hideConfirmDaysModal = useCallback(() => {
    setConfirmDaysOfflineModal(false);
    setLastDay('');
    setPopupMessage('');
  }, []);

  const confirmOfflineDays = useCallback(() => {
    if (lastDay) {
      dispatch(changeIsOpen(lastDay, false));
    }
    dispatch(toggleEverythingOffline(false, 'day'));

    setConfirmDaysOfflineModal(false);
    setLastDay('');
    setPopupMessage('');
    setIsSalonOnline(false);
    setHasChanged(true);
    setHasOpenDay(false);

    setTimeout(() => {
      setIsSalonOnline(false);
      saveChanges(false);
      setConfirmOfflineModal(false);
      setPopupMessage('');
    }, 1);
  }, [lastDay, dispatch, saveChanges]);

  const hideConfirmOfflineModal = useCallback(() => {
    setConfirmOfflineModal(false);
    setPopupMessage('');
  }, []);

  const confirmOfflineSalon = useCallback(() => {
    setTimeout(() => {
      setIsSalonOnline(false);
      saveChanges(false);
      setConfirmOfflineModal(false);
      setPopupMessage('');
    }, 1);
  }, [saveChanges]);

  const hideConfirmServicesModal = useCallback(() => {
    setConfirmServicesOfflineModal(false);
    setPopupMessage('');
  }, []);

  const hideConfirmCategoryModal = useCallback(() => {
    setConfirmCategoryModal(false);
    setPopupMessage('');
  }, []);

  const confirmOfflineServices = useCallback(() => {
    dispatch(toggleEverythingOffline(false, 'service'));
    setConfirmServicesOfflineModal(false);
    setPopupMessage('');
    setIsSalonOnline(false);
    setHasOnlineService(false);

    setTimeout(() => {
      setIsSalonOnline(false);
      saveChanges(false);
      setConfirmOfflineModal(false);
      setPopupMessage('');
    }, 1);
  }, [dispatch, saveChanges]);

  const confirmOfflineCategory = useCallback(() => {
    dispatch(toggleEverythingOffline(false, 'service'));
    setConfirmCategoryModal(false);
    setPopupMessage('');
    setHasChanged(true);
    setIsSalonOnline(false);
    setHasOnlineService(false);

    setTimeout(() => {
      setIsSalonOnline(false);
      saveChanges(false);
      setConfirmOfflineModal(false);
      setPopupMessage('');
    }, 1);
  }, [dispatch, saveChanges]);

  const toggleAlertModal = useCallback(() => {
    setIsAlertModalOpen((prev) => !prev);
  }, []);

  const handleCloseSetupOnlineBooking = useCallback(() => {
    setConfirmOnlineModal(false);
  }, []);

  const onToggleStylistDetails = useCallback((stylistId) => {
    setIsHiddenMap((prev) => ({
      ...prev,
      [stylistId]: !prev[stylistId],
    }));
  }, []);

  const handleOpenStripeNoticeModal = useCallback((stylistId) => {
    setRenderStripeSetupNoticeModal(true);
    setSelectedStylistId(stylistId);
    L.debug('SetUpView', 'handleOpenStripeNoticeModal()', 'stylist ID', stylistId);
  }, []);

  // UNSAFE_componentWillMount equivalent - runs before first render via initial state + this effect
  useEffect(() => {
    pageScrollToTop();
    if (!settingStarted || areSettingsSaved) {
      setTimeout(() => {
        L.unignore('componentWillMount()', 'getSalonOnlineSettings:', dispatch(getSalonOnlineSettings(salonId)));
        L.unignore('componentWillMount()', 'getCategories:', dispatch(getCategories(salonId)));
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidMount equivalent
  useEffect(() => {
    fetchSettingsNcob();

    delayed(DELAY.autohide, () => void updateContainerLengths());

    window.addEventListener('resize', updateContainerLengths);

    dispatch(fetchStylistsBySalon(salonId));
    subscribe();

    // initialize all stylists in the state and add them to isHiddenMap with an initial value true
    const stylistsEditMap = {};
    for (const stylist of noNilArray(stylists)) {
      stylistsEditMap[stylist.id] = true;
    }
    setIsHiddenMap(stylistsEditMap);

    window.addEventListener('beforeunload', (event) => {
      event.preventDefault();
    });

    L.debug('SetUpView', 'componentDidMount()', errorMessageRedux);

    return () => {
      // componentWillUnmount
      window.removeEventListener('resize', updateContainerLengths);
      unsubscribe();
      L.unignore('componentWillUnmount()', 'getSalonOnlineSettings:', dispatch(getSalonOnlineSettings(salonId)));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UNSAFE_componentWillReceiveProps equivalent
  const prevSettingsDataRef = useRef(settingsData);
  useEffect(() => {
    if (prevSettingsDataRef.current !== settingsData || hasChanged) {
      if (!componentFirstMounted) {
        setComponentFirstMounted(true);
      }
      const data = generateDates(settingsData, t);
      let newHasOpenDay = false;
      if (data.dayCount > 0) {
        newHasOpenDay = true;
      }
      let newHasOnlineService = false;
      if (countOnlineServices(settingsData) > 0) {
        newHasOnlineService = true;
      }
      setDayCount(data.dayCount);
      setDayItems(data.dayItems);
      setHasOpenDay(newHasOpenDay);
      setHasOnlineService(newHasOnlineService);
      setStripeModal(false);
      setIsSalonOnline(data.dayCount > 0 && newHasOnlineService && settingsData.online_booking);
    }
    prevSettingsDataRef.current = settingsData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsData, hasChanged]);

  // Render helpers
  const servicesLink = linkTo({ pattern: LINK.services, params: { salonId }, fault: '#' });

  const isOneStylist = stylists.filter((s) => s.has_column && s.status !== 'deleted' && !s.serves_as_waitlist).length;
  const businessHoursArray = Object.entries(settingsData.business_hours);

  const HEADER_SETTINGS = (
    <div className={CN.flex}>
      <div className={CN.settingsTitle}>{t('onlineBooking.generalSettings.socialWebsiteLinks.foldoutTitle')}</div>
      <Badge variant={isSalonOnline ? 'success' : 'draft'} disabled={!isSalonOnline} height="sm">
        <div className={CN.gapSmall}>{isSalonOnline ? t('onlineBooking.generalSettings.online') : t('onlineBooking.generalSettings.offline')}</div>
      </Badge>
    </div>
  );

  const HEADER_HOURS = (
    <div className={CN.flex}>
      <div className={CN.space}>{t('onlineBooking.generalSettings.businessOnlineHours.foldoutTitle')} </div>
      <div className={CN.daysContainer}>
        {businessHoursArray.map(([day, { is_open: isOpenDay }]) => (
          <div
            key={day}
            className={cn({
              [CN.day]: true,
              [CN.active]: isOpenDay,
            })}
          >
            {t(getFullDayName(day)).charAt(0)}
          </div>
        ))}
      </div>
    </div>
  );

  const STYLIST_DATA = Object.keys(settingsData.stylist_service_data);
  const isAllStylistVisible = childContainerLength > parentContainerLength;

  const renderedTitle = (
    <div className={CN.stylistWrap}>
      <div className={CN.staffTitle}>{t('onlineBooking.generalSettings.staffOnlineServices.foldoutTitle')}</div>
      <div className={CN.parentContainer} ref={parentContainerRef}>
        <div
          ref={childContainerRef}
          className={cn({
            [CN.childContainer]: true,
          })}
        >
          {STYLIST_DATA.map((stylistId, index) => {
            const stylist = settingsData.stylist_service_data[stylistId];

            const name = fromAnyToTrimmed(asObject(stylist).name);
            const shortName = stylist.name.substring(0, 2);

            let activeServices = 0;

            const keys = stylist.categories ? Object.keys(stylist.categories) : [];

            for (let j = 0; j < keys.length; j += 1) {
              const categoryId = keys[j];
              const services = stylist.categories[categoryId];

              for (let i = 0; i < services.length; i += 1) {
                if (services[i].is_online) {
                  activeServices += 1;
                }
              }
            }
            const renderedTooltipStylistName = (
              <div>
                <div className={CN.cutText}> {name}</div>
                {t('onlineBooking.generalSettings.staffOnlineServices.is')}
                {activeServices === 0 ? t('onlineBooking.generalSettings.online') : t('onlineBooking.generalSettings.offline')}
              </div>
            );

            return (
              <div
                key={createNodeKey({ index, name })}
                className={cn({
                  [CN.circle]: true,
                  [CN.isOff]: activeServices === 0,
                })}
              >
                <Tooltip variant="right" width="s2" content={renderedTooltipStylistName}>
                  <div className={CN.tooltipText}>{shortName}</div>
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
      {isAllStylistVisible && <div className={CN.countLabel}>{t('onlineBooking.generalSettings.staffOnlineServices.countLabel')}</div>}
    </div>
  );

  const componentClasses = cn({
    [CN.component]: true,
    [CN.darkMode]: isDarkMode,
  });

  const transferPolicy = TRANSFER_POLICY_MAP[stripeAccountStatus[selectedStylistId]?.transfer_policy] || null;

  const stylistStripeStatus = stripeAccountStatus[selectedStylistId]?.stripe_account_status;
  const isSetupIncomplete = selectedStylistId?.stripe_account_status === STRIPE_STATUS.incomplete;

  const tipsFoldoutIcon = CURRENCY_BORDER_ICONS[currentSalon?.country?.code];

  return (
    <div data-bem="SetupView--online-booking" className={componentClasses}>
      <ModalSetupOnlineBooking isOpen={confirmOnlineModal && !has_completed_online_boarding} onClose={handleCloseSetupOnlineBooking} onClick={handleCloseSetupOnlineBooking} />

      <SetupSelfEmployedModal
        stylistId={selectedStylistId}
        isOpen={isSetupSelfEmployedRendered}
        transferPolicy={transferPolicy}
        onClose={() => void setIsSetupSelfEmployedRendered(false)}
        onClick={(tp) => setupStripeAccount(selectedStylistId, tp)}
        determineSetupButtons={stylistStripeStatus}
      />

      {confirmDaysOfflineModal && (
        <ConfirmationModal
          popupTitle={t('onlineBooking.generalSettings.confirmationModal.title')}
          popupMessage={popupMessage}
          btnConfirmName={t('onlineBooking.generalSettings.confirmationModal.confirmButton')}
          btnDiscardName={t('onlineBooking.generalSettings.confirmationModal.discardButton')}
          discard={hideConfirmDaysModal}
          confirm={confirmOfflineDays}
          closeModal={hideConfirmDaysModal}
        />
      )}

      {confirmServicesOfflineModal && (
        <ConfirmationModal
          popupTitle={t('onlineBooking.generalSettings.confirmationModal.title')}
          popupMessage={popupMessage}
          btnConfirmName={t('onlineBooking.generalSettings.confirmationModal.confirmButton')}
          btnDiscardName={t('onlineBooking.generalSettings.confirmationModal.discardButton')}
          discard={hideConfirmServicesModal}
          confirm={confirmOfflineServices}
          closeModal={hideConfirmServicesModal}
        />
      )}

      {confirmCategoryModal && (
        <ConfirmationModal
          popupTitle={t('onlineBooking.generalSettings.confirmationModal.title')}
          popupMessage={popupMessage}
          btnConfirmName={t('onlineBooking.generalSettings.confirmationModal.confirmButton')}
          btnDiscardName={t('onlineBooking.generalSettings.confirmationModal.discardButton')}
          discard={hideConfirmCategoryModal}
          confirm={confirmOfflineCategory}
          closeModal={hideConfirmCategoryModal}
        />
      )}

      {confirmOfflineModal && (
        <ConfirmationModal
          popupTitle={t('onlineBooking.generalSettings.confirmationModal.title')}
          popupMessage={popupMessage}
          btnConfirmName={t('onlineBooking.generalSettings.confirmationModal.confirmButton')}
          btnDiscardName={t('onlineBooking.generalSettings.confirmationModal.discardButton')}
          discard={hideConfirmOfflineModal}
          confirm={confirmOfflineSalon}
          closeModal={hideConfirmOfflineModal}
        />
      )}

      {errorMessageRedux && RENDER_ERROR_MODAL && (
        <PopupModalContainer
          popupTitle={t('onlineBooking.generalSettings.errorModal.title1')}
          popupMessage={errorMessageRedux}
          buttonText={t('onlineBooking.generalSettings.errorModal.confirmButton')}
          buttonCallback={() => dispatch(clearErrorMessage())}
        />
      )}

      <NoBillingModal isOpen={isAlertModalOpen || showBillingPop} onClose={toggleAlertModal} />

      <StripeSetupNoticeModal
        isOpen={renderStripeSetupNoticeModal}
        onClose={() => setRenderStripeSetupNoticeModal(false)}
        buttonText={isSetupIncomplete ? t('onlineBooking.generalSettings.completeSetup') : t('onlineBooking.generalSettings.setupStripe')}
        onClick={isSetupIncomplete ? () => setupStripeAccount(selectedStylistId) : () => showSelfEmployedModal(selectedStylistId)}
      />

      {errorEnableModal && (
        <PopupModalContainer
          popupTitle={t('onlineBooking.generalSettings.errorModal.title1')}
          popupMessage={enableMessage}
          buttonText={t('onlineBooking.generalSettings.errorModal.confirmButton')}
          buttonCallback={closeErrorModal}
        />
      )}

      {/* Business Stripe Account & Deposits */}
      <div data-bem="SetUpView__business-stripe--foldout" className={CN.foldoutContainer}>
        <div className={CN.foldoutHeader} role="button" onClick={() => dispatch(feBusinessStripeFoldToggle())}>
          <div className={CN.foldoutTitle}>
            <img src={ICO_PAYMENT} alt="" className={CN.foldoutIcon} />
            <div className={CN.title}>
              {t('onlineBooking.generalSettings.businessStripeAccount.foldoutTitle')}
              <StripeStatusBadges tag="SetUpView" />
            </div>
          </div>

          <Icon tag="SetUpView__arrow" variant="arrowFoldout" selected={!isBusinessStripeFoldoutOpen} unselected={isBusinessStripeFoldoutOpen} width="l1" />
        </div>

        {isBusinessStripeFoldoutOpen && (
          <>
            <RequestDepositSetup />

            {hasNoShowProtection && <NoShowProtectionSetup onNoShowCheckboxClick={onNoShowCheckboxClick} errorEnableModal={errorEnableModal} />}

            <DepositSetup onDepositCheckboxClick={onDepositCheckboxClick} />
          </>
        )}
      </div>

      {/* Tips */}
      <div data-bem="SetUpView__tips--foldout" className={CN.foldoutContainer}>
        <div className={CN.foldoutHeader} role="button" onClick={() => dispatch(feTipsFoldToggle())}>
          <div className={CN.foldoutTitle}>
            <img src={tipsFoldoutIcon} alt="" className={CN.foldoutIcon} />
            {t('onlineBooking.generalSettings.tips.foldoutTitle')}
          </div>

          <Icon tag="SetUpView__arrow" variant="arrowFoldout" selected={!isTipsFoldoutOpen} unselected={isTipsFoldoutOpen} width="l1" />
        </div>

        {isTipsFoldoutOpen && (
          <div className={CN.foldoutContent}>
            <StaffTips />
          </div>
        )}
      </div>

      {/* Staff Stripe Accounts & Online Services */}
      <div data-bem="SetUpView__stylist-foldout" className={CN.foldoutContainer}>
        <div className={CN.foldoutHeader} role="button" onClick={() => dispatch(feStripeFoldToggle())}>
          <div className={CN.foldoutTitle}>
            <img src={ICO_SERVICES} alt="" className={CN.foldoutIcon} />
            {renderedTitle}
          </div>

          <Icon tag="SetUpView__arrow" variant="arrowFoldout" selected={!isStripeFoldoutOpen} unselected={isStripeFoldoutOpen} width="l1" />
        </div>

        {isStripeFoldoutOpen && (
          <div className={CN.foldoutContent}>
            <div>
              {DISPLAY_BUTTON && (
                <Button variant="emphasis" onClick={refreshStylistData}>
                  {t('onlineBooking.generalSettings.staffOnlineServices.refreshData')}
                </Button>
              )}
            </div>

            <div className={CN.subTitle}></div>

            <div data-bem="SetUpView__table-header" className={CN.dropdownHeader}>
              <div className={CN.mobileHeader}>{t('onlineBooking.generalSettings.staffOnlineServices.tableHeaders.onlineBooking')}</div>
              <div className={CN.colName}>{t('onlineBooking.generalSettings.staffOnlineServices.tableHeaders.staffMember')}</div>
              <div className={CN.colService}>{t('onlineBooking.generalSettings.staffOnlineServices.tableHeaders.bookableServices')}</div>
              <div className={CN.colButton}>{t('onlineBooking.generalSettings.staffOnlineServices.tableHeaders.stripAccount')}</div>
              <div className={CN.colTips}>{t('onlineBooking.generalSettings.staffOnlineServices.tableHeaders.takings')}</div>
            </div>

            {STYLIST_DATA.map((stylistId) => {
              const stylist = settingsData.stylist_service_data[stylistId];
              const stylistTransferPolicy = stylist?.transfer_policy || null;

              const name = fromAnyToTrimmed(stylist?.name) || t('onlineBooking.generalSettings.staffOnlineServices.name');
              const isSelf =
                stylist && stylist.is_self_employed ? t('onlineBooking.generalSettings.staffOnlineServices.selfEmployed') : t('onlineBooking.generalSettings.staffOnlineServices.employed');
              const BUTTON_NAME = stylist.stripe_account_status === STRIPE_STATUS.incomplete ? t('onlineBooking.generalSettings.completeSetup') : t('onlineBooking.generalSettings.setupStripe');

              let role = 'Stylist';
              if (stylist.role && stylist.role.trim()) {
                ({ role } = stylist);
              }

              let activeServices = 0;
              let allServices = 0;

              const keys = fromObjectToKeys(stylist?.categories ?? {});

              for (let j = 0; j < keys.length; j += 1) {
                const categoryId = keys[j];
                const services = stylist.categories[categoryId];

                for (let i = 0; i < services.length; i += 1) {
                  if (services[i]) {
                    allServices += 1;
                  }
                  if (services[i].is_online) {
                    activeServices += 1;
                  }
                }
              }

              return (
                <div key={stylistId}>
                  <DropdownService
                    stylistFace={IMG_AVATAR}
                    generalName={name}
                    isSelf={isSelf}
                    className={CN.item}
                    subName={role}
                    serviceCount={<PieChart activeServices={activeServices} allServices={allServices} />}
                    stripeStatus={stylist.stripe_account_status}
                    category_services={settingsData.category_services}
                    stylist={stylist}
                    singleCompleted={STYLIST_DATA.length === 1 && hasStripeAccount}
                    stylistId={parseInt(stylistId, 10)}
                    disabled={!isSalonOnline}
                    onCheckboxClick={onStylistServiceCheckboxClick}
                    onSwitcherClick={stylistCategoryChangeIsOnlineHandler}
                    onToggle={() => onToggleStylistDetails(stylistId)}
                    isEditMode={isHiddenMap[stylistId] ?? true}
                    changedCategories={[]}
                    descriptionChanged={false}
                    isOff={activeServices === 0}
                    isOneStylist={isOneStylist === ONE_STYLIST}
                    onEditTransferPolicy={() => {
                      setIsSetupSelfEmployedRendered(true);
                      setSelectedStylistId(stylistId);
                    }}
                    transferPolicy={stylistTransferPolicy}
                  >
                    {isOneStylist > ONE_STYLIST ? (
                      <div>
                        {fromAnyToBool(stylist.stripe_account) ? (
                          <Button variant="stripped" onClick={() => viewStripeDashboard(stylistId)}>
                            <div className={CN.colorButton}>{t('onlineBooking.generalSettings.staffOnlineServices.stripeLogin')}</div>
                          </Button>
                        ) : (
                          <div>
                            {hasStripeAccount ? (
                              <Button variant="outline" onClick={() => handleOpenStripeNoticeModal(stylistId)}>
                                <div className={CN.buttonSpace}>{BUTTON_NAME}</div>
                              </Button>
                            ) : (
                              <Tooltip variant="topLeft" content={t('onlineBooking.generalSettings.staffOnlineServices.tooltip')}>
                                <Button variant="primary" disabled={!hasStripeAccount}>
                                  <div className={CN.buttonSpace}>{t('onlineBooking.generalSettings.staffOnlineServices.stripeLogin')}</div>
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={CN.oneStylistLabel}>{t('onlineBooking.generalSettings.staffOnlineServices.stripeAccountStatus')}</div>
                    )}
                  </DropdownService>
                </div>
              );
            })}

            {STYLIST_DATA.length === 0 && (
              <div className={CN.alertOuter}>
                <AlertPanel tag="SetUpView__alertPanel__no-stylists" variant="warning">
                  <div className={CN.noStylistContainer}>
                    <div className={CN.noStylistTitle}>{t('onlineBooking.generalSettings.staffOnlineServices.noStylistTitle')}</div>
                    <div className={CN.noStylistText}>{t('onlineBooking.generalSettings.staffOnlineServices.noStylistMessage')}</div>
                    <NavLink className={CN.noStylistLink} to={servicesLink}>
                      {t('onlineBooking.generalSettings.staffOnlineServices.noStylistLink')}
                    </NavLink>
                  </div>
                </AlertPanel>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Days Bookable Online */}
      <Foldout isOpen={false} title={HEADER_HOURS} src={ICO_HOURS}>
        <div className="business-hours">
          <DropdownServiceWithoutSwitcher
            isOffline={!isSalonOnline || dayCount === 0}
            subName=""
            parentItems={[
              {
                id: 1,
                name: t('onlineBooking.generalSettings.businessOnlineHours.daysAndHours'),
                days: dayItems,
              },
            ]}
            parentNamePropName=""
            childItemsPropName="days"
            childItemCheckPropName="is_open"
            disabled={false}
            onCheckboxClick={onDayCheckboxClick}
          />
          <div className={CN.infoLink}>
            {t('onlineBooking.generalSettings.businessOnlineHours.guide')}
            <NavLink to={linkTo({ pattern: LINK.salonHours, params: { salonId }, fault: '#' })} activeClassName="active" className={CN.item} onClick={undefined}>
              <Space> {t('onlineBooking.generalSettings.businessOnlineHours.linkLabel')} </Space>
            </NavLink>
          </div>
        </div>
      </Foldout>

      {/* Booking Link & Social Integrations */}
      <Foldout isOpen={false} title={HEADER_SETTINGS} src={ICO_BOOKING}>
        <BookingCode
          isChecked={isSalonOnline}
          statusOffline="on"
          salonName={currentSalon.name}
          salonGroupName={currentSalon.group_name}
          url={ncob_tiny_url}
          group_ncob_tiny_url={group_ncob_tiny_url}
          onCheckboxClick={hasBetaAnnualSalon || (!hasBetaAnnualSalon && hasBilling) ? onSalonCheckBoxClick : toggleAlertModal}
        />
        {isSalonInGB && (
          <ToggleFeature flag="fbConnect">
            <FacebookConnect
              salonId={salonId.toString()}
              salonName={currentSalon.name}
              removeFBIntegration={(...args) => dispatch(removeFBIntegration(...args))}
              url={ncob_tiny_url}
              group_ncob_tiny_url={group_ncob_tiny_url}
              fb_details={currentSalon.fb_details}
            />
          </ToggleFeature>
        )}
      </Foldout>

      {/* Service Descriptions */}
      <Foldout isOpen={false} title={t('onlineBooking.generalSettings.serviceDescription.foldoutTitle')} src={ICO_DESCRIPTION}>
        <ServicesDescription />
      </Foldout>

      {/* Services Requiring a Patch Test */}
      <DropdownPatchTest
        category_services={settingsData.category_services}
        subName={t('onlineBooking.generalSettings.patchTestDropdown.subName')}
        isOffline={!isSalonOnline || dayCount === 0}
        parentItems={settingsData.category_services}
        parentNamePropName={t('onlineBooking.generalSettings.patchTestDropdown.patchTest')}
        childItemsPropName="services"
        childItemCheckPropName="patch_test"
        disabled={!hasOpenDay}
        onCheckboxClick={!hasBilling && !hasBetaAnnualSalon ? toggleAlertModal : onPatchTestCheckboxClick}
      />
      {stripeModal ? (
        <StripeModal>
          <div className={CN.textStripeModal}>{t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}</div>
        </StripeModal>
      ) : null}

      {USE_POLICY_SECTION && (
        <Foldout isOpen={false} title={t('onlineBooking.generalSettings.cancellationPolicy.foldoutTitle')} src={ICO_SETTINGS}>
          <div data-bem="SetUpView__cancellation-policy" className={CN.container}>
            <div>
              <div className={CN.title}>{t('onlineBooking.generalSettings.cancellationPolicy.title')}</div>
              <div className={CN.text}>{salon_ncob_text}</div>
              <div className={CN.negativeGap}>
                <ExternalLink link={LINK_POLICY} variant="secondary">
                  <small>{t('onlineBooking.generalSettings.cancellationPolicy.linkLabel')}</small>
                </ExternalLink>
              </div>
            </div>
          </div>
        </Foldout>
      )}
    </div>
  );
};

export default SetUpView;
